import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { GlobalContext } from "../Context/globalContext";
import FirebaseApp from "../firebase";
import { get, onValue, ref, remove, set } from "firebase/database";
import { BsArrowLeft } from "react-icons/bs";

const LANGUAGES = [
    { code: "en", label: "English" },
    { code: "he", label: "Hebrew" },
    { code: "nl", label: "Dutch" },
];

const MESSAGE_TYPES = [
    { key: "welcomeMessage", label: "Welcome Message" },
    { key: "instructionMessage", label: "Instruction Message" },
    { key: "thankyouMessage", label: "Thank You Message" },
];

const SECTION_OPTIONS = [
    { value: "pre", label: "Pre Questionnaire" },
    { value: "post", label: "Post Questionnaire" },
];

const createEmptyLanguageSlots = () => {
    const slots = {};
    LANGUAGES.forEach(({ code }) => {
        slots[code] = {};
    });
    return slots;
};

const createInitialQuestionStructure = () => ({
    pre: createEmptyLanguageSlots(),
    post: createEmptyLanguageSlots(),
});

const buildMessageTemplates = (source = {}) => {
    return MESSAGE_TYPES.reduce((acc, message) => {
        const sourceMessage = source[message.key] || {};
        acc[message.key] = LANGUAGES.reduce((langAcc, { code }) => {
            const langSource = sourceMessage[code] || {};
            langAcc[code] = {
                title: langSource.title || "",
                text: langSource.text || "",
            };
            return langAcc;
        }, {});
        return acc;
    }, {});
};

const createInitialMessages = () => buildMessageTemplates();

const updateTemplateField = (setter, messageKey, langCode, field, value) => {
    setter((prev) => ({
        ...prev,
        [messageKey]: {
            ...prev[messageKey],
            [langCode]: {
                ...prev[messageKey][langCode],
                [field]: value,
            },
        },
    }));
};

const MessageFields = ({ templates, setTemplates, helperText }) => (
    <div className="mb-3">
        <div className="small text-muted mb-2">{helperText}</div>
        {MESSAGE_TYPES.map((message) => (
            <div key={message.key} className="border rounded p-3 mb-3">
                <div className="fw-semibold mb-2">{message.label}</div>
                {LANGUAGES.map((lang) => (
                    <Row key={`${message.key}-${lang.code}`} className="g-3 mb-2">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>{lang.label} Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={templates[message.key][lang.code].title}
                                    onChange={(e) =>
                                        updateTemplateField(setTemplates, message.key, lang.code, "title", e.target.value)
                                    }
                                />
                            </Form.Group>
                        </Col>
                        <Col md={8}>
                            <Form.Group>
                                <Form.Label>{lang.label} Text</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={templates[message.key][lang.code].text}
                                    onChange={(e) =>
                                        updateTemplateField(setTemplates, message.key, lang.code, "text", e.target.value)
                                    }
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                ))}
            </div>
        ))}
    </div>
);

const createDefaultNewQuestion = () => ({
    section: "pre",
    lang: "en",
    text: "",
    type: "single_choice",
    required: true,
    currentOption: "",
    options: [],
});

export const Research = () => {
    const navigate = useNavigate();
    const globalContext = useContext(GlobalContext);
    const [researchTypes, setResearchTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newResearchKey, setNewResearchKey] = useState("");
    const [newResearchTitle, setNewResearchTitle] = useState("");
    const [questions, setQuestions] = useState(createInitialQuestionStructure());
    const [newQuestion, setNewQuestion] = useState(createDefaultNewQuestion());
    const [detailQuestion, setDetailQuestion] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showQuestionModal, setShowQuestionModal] = useState(false);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [activeResearch, setActiveResearch] = useState(null);
    const [editingResearch, setEditingResearch] = useState(null);
    const [messageTemplates, setMessageTemplates] = useState(createInitialMessages());
    const [editMessageTemplates, setEditMessageTemplates] = useState(createInitialMessages());

    const resetQuestionBuilders = () => {
        setQuestions(createInitialQuestionStructure());
        setNewQuestion(createDefaultNewQuestion());
    };

    const buildOptionsObject = (optionsArray) => {
        return optionsArray.reduce((acc, option, index) => {
            const trimmedKey = option.key || `o${index + 1}`;
            acc[trimmedKey] = option.label;
            return acc;
        }, {});
    };

    const mapLanguages = (sectionData) => {
        const mapped = {};
        LANGUAGES.forEach(({ code }) => {
            mapped[code] = sectionData[code] || {};
        });
        return mapped;
    };

    const buildStateFromQuestionnaires = (questionnaires = {}) => {
        const result = createInitialQuestionStructure();
        ["pre", "post"].forEach(section => {
            const languages = questionnaires[section]?.languages || {};
            LANGUAGES.forEach(({ code }) => {
                result[section][code] = {
                    ...languages[code]
                };
            });
        });
        return result;
    };

    const getNextQuestionKey = (state, section, lang) => {
        let index = 1;
        const languageQuestions = state[section][lang] || {};
        while (languageQuestions[`q${index}`]) {
            index += 1;
        }
        return `q${index}`;
    };

    const buildQuestionnairePayload = (state = questions) => ({
        pre: {
            languages: mapLanguages(state.pre)
        },
        post: {
            languages: mapLanguages(state.post)
        }
    });

    const persistQuestions = (state) => {
        if (!activeResearch?.id) {
            return;
        }
        FirebaseApp()
            .then((db) => {
                const researchRef = ref(db, `ResearchType/${activeResearch.id}`);
                return get(researchRef).then((snapshot) => {
                    const current = snapshot.val() || {};
                    return set(researchRef, {
                        ...current,
                        questionnaires: buildQuestionnairePayload(state)
                    });
                });
            })
            .catch((err) => {
                console.error("Error updating questionnaires:", err);
                toast.error("Failed to save questionnaire.");
            });
    };

    const closeMessageEditor = () => {
        setShowMessageModal(false);
        setEditingResearch(null);
        setEditMessageTemplates(createInitialMessages());
    };

    const openMessageEditor = (research) => {
        setEditingResearch(research);
        setEditMessageTemplates(buildMessageTemplates(research?.messages));
        setShowMessageModal(true);
    };

    const handleSaveMessages = () => {
        if (!editingResearch?.id) {
            return;
        }

        FirebaseApp()
            .then((db) => {
                const researchRef = ref(db, `ResearchType/${editingResearch.id}`);
                return get(researchRef).then((snapshot) => {
                    const current = snapshot.val() || {};
                    return set(researchRef, {
                        ...current,
                        messages: editMessageTemplates,
                    });
                });
            })
            .then(() => {
                toast.success("Messages updated.");
                closeMessageEditor();
            })
            .catch((err) => {
                console.error("Error updating research messages:", err);
                toast.error("Failed to update messages.");
            });
    };

    const handleAddQuestion = () => {
        const { section, lang, text, type, required, options } = newQuestion;
        const trimmedText = text.trim();
        if (!trimmedText) {
            toast.error("Question text is required.");
            return;
        }

        if (!options.length) {
            toast.error("Add at least one option.");
            return;
        }

        const nextOptions = buildOptionsObject(options);

        setQuestions(prev => {
            const questionKey = getNextQuestionKey(prev, section, lang);
            const next = {
                ...prev,
                [section]: {
                    ...prev[section],
                    [lang]: {
                        ...prev[section][lang],
                        [questionKey]: {
                            text: trimmedText,
                            type,
                            required,
                            options: nextOptions
                        }
                    }
                }
            };

            persistQuestions(next);
            return next;
        });

        setNewQuestion(prev => ({
            ...prev,
            text: "",
            currentOption: "",
            options: []
        }));
    };

    const removeQuestion = (section, lang, questionKey) => {
        setQuestions(prev => {
            const updatedLanguage = { ...prev[section][lang] };
            delete updatedLanguage[questionKey];
            const next = {
                ...prev,
                [section]: {
                    ...prev[section],
                    [lang]: updatedLanguage
                }
            };
            persistQuestions(next);
            return next;
        });
    };

    const isSuperAdmin = useMemo(() => globalContext.userRole === "SuperAdmin", [globalContext.userRole]);

    useEffect(() => {
        if (!globalContext.userRole) {
            return;
        }

        if (!isSuperAdmin) {
            navigate("/", { replace: true });
            return;
        }

        let cleanup = () => { };

        FirebaseApp()
            .then((db) => {
                const researchRef = ref(db, "ResearchType");
                cleanup = onValue(
                    researchRef,
                    (snapshot) => {
                        const entries = [];
                        snapshot.forEach((childSnapshot) => {
                            entries.push({
                                id: childSnapshot.key,
                                ...childSnapshot.val(),
                            });
                        });
                        setResearchTypes(entries);
                        setLoading(false);
                    },
                    (err) => {
                        console.error("Error fetching research types:", err);
                        toast.error("Error loading research types");
                        setLoading(false);
                    }
                );
            })
            .catch((err) => {
                console.error("Error initializing Firebase for research types:", err);
                toast.error("Error loading research types");
                setLoading(false);
            });

        return () => cleanup();
    }, [globalContext.userRole, isSuperAdmin, navigate]);

    const clearModalFields = () => {
        setNewResearchKey("");
        setNewResearchTitle("");
        resetQuestionBuilders();
        setMessageTemplates(createInitialMessages());
    };

    const handleAddResearchType = () => {
        const key = newResearchKey.trim();
        const title = newResearchTitle.trim() || key.toUpperCase();

        if (!key) {
            toast.error("Research key is required.");
            return;
        }

        FirebaseApp()
            .then((db) => {
                const researchRef = ref(db, `ResearchType/${key}`);
                return get(researchRef).then((snapshot) => {
                    if (snapshot.exists()) {
                        toast.error("Research type already exists.");
                        return;
                    }

                    const payload = {
                        title,
                        questionnaires: buildQuestionnairePayload(),
                        messages: messageTemplates,
                    };
                    return set(researchRef, payload)
                        .then(() => {
                            toast.success("Research type added.");
                            clearModalFields();
                            setShowAddModal(false);
                        })
                        .catch((err) => {
                            console.error("Error adding research type:", err);
                            toast.error("Failed to add research type.");
                        });
                });
            })
            .catch((err) => {
                console.error("Error initializing Firebase for research types:", err);
                toast.error("Failed to add research type.");
            });
    };

    const openQuestionManager = (research) => {
        setActiveResearch(research);
        const nextQuestions = buildStateFromQuestionnaires(research.questionnaires);
        setQuestions(nextQuestions);
        setNewQuestion(createDefaultNewQuestion());
        setShowQuestionModal(true);
    };

    const handleDeleteResearchType = (id) => {
        if (!id) {
            return;
        }

        if (!window.confirm(`Delete research type "${id}"?`)) {
            return;
        }

        FirebaseApp()
            .then((db) => {
                const researchRef = ref(db, `ResearchType/${id}`);
                return remove(researchRef)
                    .then(() => {
                        toast.success("Research type deleted.");
                    })
                    .catch((err) => {
                        console.error("Error deleting research type:", err);
                        toast.error("Failed to delete research type.");
                    });
            })
            .catch((err) => {
                console.error("Error initializing Firebase for research types:", err);
                toast.error("Failed to delete research type.");
        });
    };

    const renderQuestionPreview = () => (
        <div className="question-preview mt-4">
            {SECTION_OPTIONS.map(section => (
                <div key={section.value} className="mb-3">
                    <h6><strong>{section.label}</strong></h6>
                    <div className="d-flex flex-wrap gap-3 mt-2">
                        {LANGUAGES.map(lang => {
                            const entries = Object.entries(questions[section.value][lang.code] || {});
                            return (
                                <div key={lang.code} className="language-grid">
                                    <div className="fw-semibold small mb-1"><strong>{lang.label}</strong></div>
                                        <ul className="list-unstyled small mb-0">
                                            {entries.length > 0 ? entries.map(([qKey, qValue]) => (
                                                <li key={qKey} className="d-flex align-items-start justify-content-between mb-1 gap-2">
                                                    <span><strong>{qKey}:</strong> {qValue.text}</span>
                                                    <div className="d-flex gap-1">
                                                        <button
                                                            type="button"
                                                            className="btn btn-link btn-sm"
                                                            onClick={() => {
                                                                setDetailQuestion({
                                                                    key: qKey,
                                                                    section: section.value,
                                                                    lang: lang.code,
                                                                    data: qValue,
                                                                });
                                                                setShowDetailModal(true);
                                                            }}
                                                        >
                                                            Details
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-link btn-sm text-danger ps-1"
                                                            onClick={() => {
                                                                if (!window.confirm(`Delete question ${qKey}?`)) {
                                                                    return;
                                                                }
                                                                removeQuestion(section.value, lang.code, qKey);
                                                            }}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                </li>
                                            )) : (
                                                <li className="text-muted">No questions</li>
                                            )}
                                        </ul>
                                    </div>
                                );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );

    const renderQuestionBuilder = () => (
        <div className="border-top pt-3 mt-3">
            <h6 className="mb-3">Add Question</h6>
            <Row className="g-3">
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Section</Form.Label>
                        <Form.Select
                            value={newQuestion.section}
                            onChange={(e) => setNewQuestion(prev => ({ ...prev, section: e.target.value }))}
                        >
                            {SECTION_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Language</Form.Label>
                        <Form.Select
                            value={newQuestion.lang}
                            onChange={(e) => setNewQuestion(prev => ({ ...prev, lang: e.target.value }))}
                        >
                            {LANGUAGES.map(lang => (
                                <option key={lang.code} value={lang.code}>
                                    {lang.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <Form.Group>
                        <Form.Label>Question Text</Form.Label>
                        <Form.Control
                            type="text"
                            value={newQuestion.text}
                            onChange={(e) => setNewQuestion(prev => ({ ...prev, text: e.target.value }))}
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Type</Form.Label>
                        <Form.Select
                            value={newQuestion.type}
                            onChange={(e) => setNewQuestion(prev => ({ ...prev, type: e.target.value }))}
                        >
                            <option value="single_choice">Single Choice</option>
                            <option value="multi_choice">Multi Choice</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="d-flex align-items-center mt-3">
                        <Form.Check
                            type="switch"
                            id="question-required"
                            label="Required"
                            checked={newQuestion.required}
                            onChange={(e) => setNewQuestion(prev => ({ ...prev, required: e.target.checked }))}
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>New Option</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control
                                type="text"
                                value={newQuestion.currentOption}
                                onChange={(e) => setNewQuestion(prev => ({ ...prev, currentOption: e.target.value }))}
                            />
                            <Button
                                variant="outline-secondary"
                                onClick={() => {
                                    const trimmed = newQuestion.currentOption.trim();
                                    if (!trimmed) {
                                        return;
                                    }
                                    setNewQuestion(prev => ({
                                        ...prev,
                                        currentOption: "",
                                        options: [...prev.options, { label: trimmed }]
                                    }));
                                }}
                            >
                                Add
                            </Button>
                        </div>
                        <ul className="list-unstyled small mt-2">
                            {newQuestion.options.map((option, idx) => (
                                <li key={`${option.label}-${idx}`} className="d-flex align-items-center justify-content-between">
                                    <span>{option.label}</span>
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm text-decoration-none"
                                        onClick={() => {
                                            setNewQuestion(prev => ({
                                                ...prev,
                                                options: prev.options.filter((_, i) => i !== idx)
                                            }));
                                        }}
                                    >
                                        Remove
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <Button variant="outline-primary" type="button" onClick={handleAddQuestion}>
                        Add Question
                    </Button>
                </Col>
            </Row>
        </div>
    );
    const renderContent = () => {
        if (loading) {
            return <p>Loading research types…</p>;
        }

        if (!researchTypes.length) {
            return <p>No research types found.</p>;
        }

        return (
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>Research Type</th>
                        <th>Title</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {researchTypes.map((type) => (
                        <tr key={type.id || type.title}>
                            <td>{type.id}</td>
                            <td>{type.title || type.id?.toUpperCase()}</td>
                            <td>
                                <Button
                                    size="sm me-2"
                                    variant="outline-secondary"
                                    onClick={() => openMessageEditor(type)}
                                >
                                    Edit Messages
                                </Button>
                                <Button size="sm me-2" variant="outline-primary" onClick={() => openQuestionManager(type)}>
                                    Manage Questions
                                </Button>
                                <Button
                                    size="sm"
                                    variant="danger"
                                    onClick={() => handleDeleteResearchType(type.id)}
                                >
                                    Delete
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    return (
        <main id="main" className="main">
            <div className="pagetitle">
                <Row className="align-items-center mb-3">
                    <Col>
                        <h1>
                            <button
                                type="button"
                                className="back-link-icon"
                                aria-label="Go back"
                                onClick={() => navigate(-1)}
                            >
                                <BsArrowLeft aria-hidden="true" />
                            </button>
                            Research Types
                        </h1>
                    </Col>
                    <Col className="text-end">
                        <Button onClick={() => setShowAddModal(true)}>Add Research Type</Button>
                    </Col>
                </Row>
            </div>
            <section className="section dashboard">
                {renderContent()}
            </section>

            <Modal
                show={showAddModal}
                onHide={() => {
                    setShowAddModal(false);
                    clearModalFields();
                }}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Add Research Type</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Research Key</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., phq9"
                                value={newResearchKey}
                                onChange={(e) => setNewResearchKey(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Title</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Optional title"
                                value={newResearchTitle}
                                onChange={(e) => setNewResearchTitle(e.target.value)}
                            />
                        </Form.Group>
                        <MessageFields
                            templates={messageTemplates}
                            setTemplates={setMessageTemplates}
                            helperText="Define the multilingual messages shown to participants."
                        />
                        <div className="text-muted small mb-2">Questions are managed per research type after creation.</div>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowAddModal(false);
                            clearModalFields();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleAddResearchType}>
                        Add
                    </Button>
            </Modal.Footer>
        </Modal>
        <Modal
            show={showQuestionModal}
            onHide={() => setShowQuestionModal(false)}
            size="lg"
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Manage Questions for {activeResearch?.id || "Research Type"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {activeResearch ? (
                    <>
                        {renderQuestionPreview()}
                        {renderQuestionBuilder()}
                    </>
                ) : (
                    <p>Loading questions…</p>
                )}
            </Modal.Body>
        </Modal>
        <Modal
            show={showMessageModal}
            onHide={closeMessageEditor}
            size="lg"
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    {editingResearch ? `Edit Messages for ${editingResearch.id}` : "Edit Messages"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {editingResearch ? (
                    <MessageFields
                        templates={editMessageTemplates}
                        setTemplates={setEditMessageTemplates}
                        helperText="Update the multilingual messages shown to participants."
                    />
                ) : (
                    <p>Loading messages…</p>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeMessageEditor}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSaveMessages}>
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
        <Modal
            show={showDetailModal}
            onHide={() => setShowDetailModal(false)}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>Question Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {detailQuestion ? (
                    <div className="px-2">
                        <div className="mb-2">
                            <strong>Section:</strong> {detailQuestion.section}
                        </div>
                        <div className="mb-2">
                            <strong>Language:</strong> {detailQuestion.lang}
                        </div>
                        <div className="mb-2">
                            <strong>Text:</strong> {detailQuestion.data.text}
                        </div>
                        <div className="mb-2">
                            <strong>Type:</strong> {detailQuestion.data.type}
                        </div>
                        <div className="mb-2">
                            <strong>Required:</strong> {detailQuestion.data.required ? "Yes" : "No"}
                        </div>
                        <div>
                            <strong>Options:</strong>
                            <ul className="px-5">
                                {Object.entries(detailQuestion.data.options || {}).map(([optKey, optValue]) => (
                                    <li key={optKey}>{optKey}: {optValue}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                ) : (
                    <p>No question selected</p>
                )}
            </Modal.Body>
        </Modal>
    </main>
    );
};
